/**
 * CardTypeFilter - Shared card type filter component
 *
 * Renders filter chips for MTG card main types. Used in Collection and Set Details filters.
 */
import type { ReactElement } from 'react';

import FilterChip from '@/components/FilterChip/FilterChip';
import type { CardType } from '@/types/schemas';

export interface CardTypeFilterProps {
  /** Currently selected card types */
  selectedTypes: CardType[];
  /** Callback when a type is toggled */
  onToggle: (type: CardType) => void;
}

/** Standard card type options with display labels */
const STANDARD_TYPES: { value: CardType; label: string }[] = [
  { value: 'creature', label: 'Creature' },
  { value: 'instant', label: 'Instant' },
  { value: 'sorcery', label: 'Sorcery' },
  { value: 'enchantment', label: 'Enchant' },
  { value: 'artifact', label: 'Artifact' },
  { value: 'land', label: 'Land' },
  { value: 'planeswalker', label: 'Planes.' },
  { value: 'battle', label: 'Battle' },
];

/**
 * CardTypeFilter renders filter chips for common MTG card main types.
 *
 * @example
 * <CardTypeFilter
 *   selectedTypes={filters.cardTypes}
 *   onToggle={(type) => toggleFilter('cardTypes', type)}
 * />
 */
export default function CardTypeFilter({ selectedTypes, onToggle }: CardTypeFilterProps): ReactElement {
  return (
    <>
      {STANDARD_TYPES.map((type) => (
        <FilterChip
          key={type.value}
          label={type.label}
          isActive={selectedTypes.includes(type.value)}
          onClick={() => onToggle(type.value)}
        />
      ))}
    </>
  );
}
